import React, { useEffect, useMemo, useState, useRef, useCallback } from 'react'
import MultiSelectDropdown from 'components/MultiSelectDropdown/MultiSelectDropdown'
import { Body, Drawer } from 'components/Shell/Shell'
import { NavLink } from 'react-router-dom'
import { shallowEqual, useSelector, useDispatch } from 'react-redux'
import {
  fetchAndSetBOSearchResults,
  fetchAndSetMiscBOSearchResults,
  checkDLNValid,
  fetchAndSetDLNSearchResults,
} from './BOSearchActions'
import { overrideSidebarResizing, setSuccessToast, toggleSidebarCollapse } from '../../actions'
import { Spinner } from '../Loaders/Loaders'
import {
  Accordion,
  Checkbox,
  DatePicker,
  RadioGroup,
  SelectBox,
  TextInput,
} from '@tecuity/granite-ui'
import { GraniteDataGrid } from '@tecuity/granite-ui/GraniteDataGrid'
import { IconButton } from '@tecuity/granite-ui/IconButton'
import FileDownloader from 'components/ImageViewer/FileDownloader'
import IconCalendar from '@tecuity/sos-icons/react/calendar'
import IconSearch from '@tecuity/sos-icons/react/search'
import CrossIcon from '@tecuity/sos-icons/react/cross-heavy'
import format from 'date-fns/format'
import './BOSearch.scss'
import isValid from 'date-fns/isValid'
import EmptySearchPlaceholder from '../Search/EmptySearchPlaceholder'
import {
  FETCH_SEARCH_OPTIONS,
  BO_SEARCH_COUNT,
  BO_SEARCH_GET_CSV_RESULTS,
  GET_STAFF_SEARCH_SETTINGS,
  GET_MISC_STAFF_SEARCH_FIELDS,
  GET_MISC_STAFF_SEARCH_CSV_RESULTS,
} from '../../requests'
import SearchItemInfo from './SearchItemInfo'
import MiscSearchGrid from './MiscSearchGrid'
import { openSpecificImage } from '../ImageViewer/ImageViewerActions'
import { setErrorToast } from '../../actions'
import {
  SET_BO_SEARCH_RESULTS_PAGE,
  CLEAR_BO_SEARCH_ERRORS,
  SET_PAGINATED_ROW_COUNTS,
  SET_SERVER_PAGE,
  SET_ROWS_PER_PAGE,
  SET_SERVER_PAGINATION,
  SET_STAFF_SEARCH_JSON,
} from './BOSearchReducer'
import DownloadBox from '@tecuity/sos-icons/react/download-box'
import ErrorBox from '../Search/ErrorBox'
import { getStateOptions, getCountryOptions } from '../Wizard2/selectOptions'
import { useLocation } from 'react-router'
import { SET_CORRESPONDENCE_SEARCH_ITEM } from '../WVMCorrespondence/Correspondence2Reducer'
import moment from 'moment'
import { SUBROUTES } from '../../App'

const isMiscSearch = (fields) => Boolean(fields?.IsMiscRecordsType)

const BOSearch = ({ match, history, sublinks, updateTitle }) => {
  const dispatch = useDispatch()
  const location = useLocation()
  const {
    error,
    errorDetails,
    searchParameters,
    searchResults,
    csvSearchResults,
    page,
    serverPage,
    rowsPerPage,
    serverPagination,
    paginatedRowCounts,
    miscSearchTemplates,
    searchJson = [],
    lastSearchName,
    lastUsedParameters,
  } = useSelector((state) => state.boSearch, shallowEqual)
  const { loggedIn, appUserRights } = useSelector((state) => state.auth, shallowEqual)
  const [searchFields, setSearchFields] = useState({})
  const [activeSearch, setActiveSearch] = useState('')
  const [loading, setLoading] = useState(true)
  const [searchFieldsError, setSearchFieldsError] = useState(false)
  const [serverPaginatedLoading, setServerPaginatedLoading] = useState(false)
  const [showItemInfo, setShowItemInfo] = useState(false)
  const [selectedRow, setSelectedRow] = useState({})
  const tableRef = useRef(null)
  const [downloadURL /*setDownloadURL*/] = useState(null)
  const [downloadOnNextTick, setDownloadOnNextTick] = useState(false)
  const [triggerSearch, setTriggerSearch] = useState({ trigger: false })
  const downloader = useRef(null)

  const lastRowSourceId = useMemo(() => {
    if (
      searchResults[activeSearch] &&
      searchResults[activeSearch].length > 0 &&
      searchResults[activeSearch][searchResults[activeSearch].length - 1]['SourceID']
    ) {
      return searchResults[activeSearch][searchResults[activeSearch].length - 1]['SourceID']
    }
    return null
  }, [searchResults, activeSearch])

  useEffect(() => {
    if (!searchJson || searchJson.length === 0) {
      setLoading(true)
      GET_STAFF_SEARCH_SETTINGS(
        (res) => {
          dispatch({
            type: SET_STAFF_SEARCH_JSON,
            searchJson: res.map((x) => ({
              ...x,
              searches: (x.searches || []).map((y) => ({
                ...y,
                SetParams: JSON.parse(y.SetParams),
              })),
            })),
          })
          setLoading(false)
        },
        (err) => {
          console.error(err)
          setLoading(false)
        }
      )
    }
  }, [])

  useEffect(() => {
    if (triggerSearch.trigger) {
      setTriggerSearch({ trigger: false })
    }
  }, [triggerSearch])

  useEffect(() => {
    if (!showItemInfo) {
      setSelectedRow({})
    }
  }, [showItemInfo])

  const mapFieldOptions = (search) => {
    if (
      search.SearchFields.every(
        (field) => !field.apiCall || field.apiCall === 'States' || field.apiCall === 'Countries'
      )
    ) {
      setSearchFields(search)
    } else {
      if (search.SearchFields) {
        search.SearchFields.map((field, index) => {
          if (field.apiCall) {
            switch (field.apiCall) {
              case 'States':
                break
              case 'Countries':
                // handled in the rendering of the SelectBox field
                break
              default:
                FETCH_SEARCH_OPTIONS({ name: search.Name, apiCall: field.apiCall }, (response) => {
                  if (Array.isArray(response)) {
                    field.options = response.map((opt) => {
                      return { 
                        ...opt, 
                        // Try to load from Id/Name just in case a BasicLookupType was passed back
                        value: opt.Id ? opt.Id.toString() : opt.value.toString(), // to prevent duplicate <option> keys
                        label: opt.Name ? opt.Name : opt.label 
                      } 
                    })
                  }
                  const newField = { ...field }
                  const newSearch = insert(search.SearchFields, index, newField)
                  const filteredSearch = newSearch.filter(
                    (item, i, a) => a.findIndex((t) => t.id === item.id) === i
                  )
                  const newCat = { ...search, SearchFields: [...filteredSearch] }
                  // Replace rather than merge into prevObject: merging would let a prior
                  // search's keys leak through. In particular a Misc search's IsMiscRecordsType/
                  // MiscellaneousModuleId/SourceTypeParam aren't present on newCat, so a spread
                  // merge kept them and made isMiscSearch() stay true — routing this regular
                  // search to the Misc endpoint. Option accumulation across async apiCall
                  // callbacks still works via in-place mutation of the shared field.options.
                  setSearchFields(newCat)
                })
            }
          }
          return null
        })
      }
    }
  }

  useEffect(() => {
    updateTitle(searchFields.Title)
  }, [searchFields])

  const insert = (arr, index, item) => {
    if (arr.length > index) {
      return [...arr, item]
    }
    return [
      ...arr.slice(0, index), // first half
      item, // items to be inserted
      ...arr.slice(index), // second half
    ]
  }

  const getSearchFields = (id) => {
    setLoading(true)
    setSearchFieldsError(false)
    if (id && id.startsWith('misc-')) {
      const moduleId = id.slice(id.lastIndexOf('-') + 1)
      const sourceType = SUBROUTES.find(
        (r) =>
          r.IsMiscRecordsType &&
          r.MiscRecordsModules.some((m) => String(m.MiscellaneousModuleId) === moduleId)
      )
      GET_MISC_STAFF_SEARCH_FIELDS(
        moduleId,
        (searchFieldsResponse) => {
          setSearchFields({
            ...searchFieldsResponse,
            IsMiscRecordsType: true,
            MiscellaneousModuleId: Number(moduleId),
            SourceTypeParam: sourceType?.parameter ?? '',
          })
          setLoading(false)
        },
        () => {
          setSearchFieldsError(true)
          setLoading(false)
        }
      )
      return
    }
    if (id) {
      setLoading(false)
      searchJson.filter((category) =>
        category.searches.filter((search) => search.id == id && mapFieldOptions(search))
      )
    }
  }

  const getSearchResults = (parameters, searchName, searchTitle, serverPaginated = false) => {
    dispatch(
      fetchAndSetBOSearchResults(
        { ...parameters, LastRowSourceId: serverPaginated ? null : lastRowSourceId },
        searchName,
        searchTitle,
        () => {
          setLoading(false)
          setServerPaginatedLoading(false)
          window.setTimeout(() => {
            scroll()
          }, 100)
          window.setTimeout(() => {
            resizeTable()
          }, 400)
        },
        (error) => {
          console.error(error)
          setLoading(false)
          setServerPaginatedLoading(false)
        }
      )
    )
  }

  const fetchWorkItems = (parameters, searchName, searchTitle, resetSearchCount = false) => {
    if (isMiscSearch(searchFields)) {
      setServerPaginatedLoading(true)
      const baseParams = {
        ...parameters,
        ModuleId: searchFields.MiscellaneousModuleId,
      }
      const miscParams = {
        ...baseParams,
        PageSize: rowsPerPage,
        PageNumber: 0,
      }
      dispatch(
        fetchAndSetMiscBOSearchResults(
          miscParams,
          searchFields.Name,
          () => {
            setLastUsedParameters(baseParams)
            setLoading(false)
            setServerPaginatedLoading(false)
          },
          () => {
            setLoading(false)
            setServerPaginatedLoading(false)
          }
        )
      )
      return
    }
    if (resetSearchCount) {
      setServerPaginatedLoading(true)
      BO_SEARCH_COUNT(
        { ...parameters, PageSize: rowsPerPage, PageNumber: page + 1 },
        searchName,
        (results) => {
          if (results) {
            dispatch({
              type: SET_PAGINATED_ROW_COUNTS,
              searchName: activeSearch,
              rowCount: results,
            })
            dispatch({
              type: SET_SERVER_PAGINATION,
              searchName: activeSearch,
              isServerPaginated: true,
            })
            getSearchResults(
              { ...parameters, PageSize: rowsPerPage, PageNumber: page + 1 },
              searchName,
              searchTitle,
              true
            )
          } else {
            dispatch({
              type: SET_SERVER_PAGINATION,
              searchName: activeSearch,
              isServerPaginated: false,
            })
            getSearchResults(
              { ...parameters, PageSize: rowsPerPage, PageNumber: page + 1 },
              searchName,
              searchTitle
            )
          }
        },
        (err) => {
          console.error(err)
        }
      )
    } else {
      setLoading(true)
      getSearchResults(parameters, searchName, searchTitle)
    }
  }

  const scroll = () => {
    tableRef && tableRef.current && tableRef.current.scrollIntoView({ behavior: 'smooth' })
  }

  const resizeTable = () => {
    const columnWidth = document
      .querySelector('.MuiDataGrid-columnHeadersInner')
      .getBoundingClientRect().width
    tableRef.current.style.maxWidth = columnWidth + 'px'
  }

  useEffect(() => {
    if (
      !loggedIn ||
      appUserRights.length === 0 ||
      ![
        'NotaryView',
        'FacsimileView',
        'ApostilleView',
        'TrademarkView',
        'UCCView',
        'BusinessView',
        'RegisteredAgentView',
        'CharityView',
        'AthleteAgentView',
        'SopView',
        'ReceiptView',
        'DataRequestView',
        'BrowseImages',
        'ViewCorrespondence',
        'OrdersView',
        'ViewUserStaffSearch',
        'MiscellaneousView',
      ].find((right) => appUserRights.includes(right))
    ) {
      history.push(sublinks[0].path)
    }
  }, [])

  useEffect(() => {
    let id = match.params.id
    if (!id) {
      const firstId = firstAvailableSearchId()
      if (firstId) {
        history.replace(`/search/back-office/${firstId}`)
      }
    }
  }, [match, searchJson])

  useEffect(() => {
    getSearchFields(match.params.id)
  }, [match.params.id, searchJson])

  useEffect(() => {
    dispatch(overrideSidebarResizing(true))
    dispatch(toggleSidebarCollapse(true))
    return () => {
      dispatch(overrideSidebarResizing(false))
      dispatch(toggleSidebarCollapse(false))
    }
  }, [])

  const firstAvailableSearchId = () => {
    const categories = (searchJson || []).map((category) => {
      return {
        ...category,
        searches: category.searches.filter((search) => {
          return doesUserHaveSearchAccess(search.Name, appUserRights)
        }),
      }
    })
    const availableCategory = categories.find((cat) => cat.searches.length > 0)
    if (availableCategory) {
      return availableCategory.searches[0].id
    }
    const firstMiscRoute = SUBROUTES.filter(
      (r) => r.IsMiscRecordsType && appUserRights.includes('MiscellaneousView')
    ).sort((a, b) => (a.label || a.Title || '').localeCompare(b.label || b.Title || ''))[0]
    if (firstMiscRoute && firstMiscRoute.MiscRecordsModules.length > 0) {
      return `misc-${firstMiscRoute.MiscRecordsModules[0].MiscellaneousModuleId}`
    }
    return undefined
  }

  const getRedirect = useCallback(
    (row) => {
      if (!row) return ''
      switch (searchFields.Name) {
        case 'Search':
          return `/recordDetails/${searchFields.Name}/${row[' Filing #']}`
        case 'TrademarkSearch':
        case 'TrademarkEntitySearch':
          return `/recordDetails/TrademarkSearch/${row['Trademark #'] || row['Record Number']}`
        case 'CustomCorrespondenceSearch':
          dispatch({ type: SET_CORRESPONDENCE_SEARCH_ITEM, row: row })
          return `/office/correspondence/${
            searchFields.SetParams.CorrespondenceType === 1 ? 'reject' : 'correct'
          }`
        case 'RegisteredAgentEntitySearch':
          return `/recordDetails/RegisteredAgentSearch/${row['File #']}`
        case 'BusinessFilingSearch':
          return `/recordDetails/BusinessEntitySearch/${row['Entity #']}`
        case 'BusinessEntitySearch':
          if (row['Record Number']) return `/recordDetails/${searchFields.Name}/${row['Record Number']}`
          if (row['Filing Number']) return `/recordDetails/${searchFields.Name}/${row['Filing Number']}`
          break
        case 'CharitableOrganizationSearch':
          return `/recordDetails/${searchFields.Name}/${row['Record #']}`
        case 'FrequentPayorSearch':
          return `/recordDetails/${searchFields.Name}/${row['Payor #']}` //`/accounting/frequentPayor/${row['Payor #']}`
        case 'ReceiptSearch':
          return `/accounting/receipt/${row['Receipt #']}`
        case 'UCCEntitySearch':
          return `/recordDetails/UCCSearch/${row['Filing #']}`
        case 'UCCInfoRequestEntitySearch':
          return `/recordDetails/${searchFields.Name}/${row['Filing #']}`
        case 'NotaryEntitySearch':
          return `/recordDetails/${searchFields.Name}/${row['Commission Number']}`
        case 'ApostilleEntitySearch':
          return `/recordDetails/${searchFields.Name}/${row['Apostille Number']}` // IS THIS CORRECT?
        case 'PartySearch':
          return `/recordDetails/BusinessEntitySearch/${row['Entity #']}`
        case 'AddressSearch':
          if (row['Entity #']) return `/recordDetails/BusinessEntitySearch/${row['Entity #']}`
          if (row['Trademark #'] || row['Record Number'])
            return `/recordDetails/TrademarkSearch/${row['Trademark #'] || row['Record Number']}`
          break
        case 'WebUserSearch':
        case 'DataRequestSearch':
        case 'LobbyistSearch':
        case 'ContractorSearch':
          return `/recordDetails/${searchFields.Name}/${row['SourceID']}`
        default:
          dispatch(setErrorToast('Staff Search Redirect Failed'))
          return location.pathname
      }
    },
    [searchFields.Name, searchFields.SetParams]
  )

  const specificColumn = (col) => {
    const defaultCol = {
      field: col,
      headerName: col,
      minWidth: 150,
      disableColumnMenu: true,
    }

    switch (col) {
      case 'IsStaff':
        return {
          ...defaultCol,
          minWidth: 50,
          renderCell: (params) => {
            return params.value ? 'Yes' : 'No'
          },
        }
      case 'Old Name(s)':
        return {
          ...defaultCol,
          hide: true,
        }
      case 'Record Number':
        if (searchFields.Name === 'TrademarkSearch') {
          return {
            ...defaultCol,
            minWidth: 160,
          }
        }
        return defaultCol
      case 'Name':
        return {
          ...defaultCol,
          minWidth: 240,
          renderCell: (params) => {
            let len = params.row[col].length
            return (
              <p>{`${params.row[col].substring(0, len > 25 ? 25 : len)}${
                len > 25 ? '...' : ''
              }`}</p>
            )
          },
        }
      case 'State':
        return {
          ...defaultCol,
          minWidth: 80,
        }
      case 'Commission Number':
        return {
          ...defaultCol,
          minWidth: 120,
          type: process.env.COMMISSION_NUM_IS_INT ? 'number' : 'string',
          align: 'left',
          headerAlign: 'left',
          valueFormatter: (params) => {
            return params.value
          },
        }
      case 'Business Phone':
        return {
          ...defaultCol,
          minWidth: 120,
        }
      case 'City':
      case 'Post Code':
        return {
          ...defaultCol,
          minWidth: 110,
        }
      case 'Date Secretary of State Accepted Service':
        return {
          ...defaultCol,
          minWidth: 180,
        }
      case 'Amendment Type':
        return {
          ...defaultCol,
          minWidth: 200,
        }
      case 'Username':
      case 'Email':
      case 'Trademark Record Type':
        return {
          ...defaultCol,
          minWidth: 280,
        }
      case 'DLN #':
      case 'DLN Number':
      case 'DLN':
        return {
          ...defaultCol,
          renderCell: (params) => {
            const dln = params.row.DLN || params.row['DLN Number'] || params.row['DLN #'] || ''
            return (
              <div
                className={dln.indexOf(',') === -1 ? 'dlnTableLink' : ''}
                tabIndex={dln.indexOf(',') === -1 ? 0 : -1}
                onClick={() => {
                  return dln.indexOf(',') === -1
                    ? checkDLNValid(
                        dln,
                        (id) => {
                          dispatch(openSpecificImage(id, dln))
                        },
                        (dln) => {
                          dispatch(setErrorToast('DLN ' + dln + ' not found'))
                        }
                      )
                    : null
                }}
              >
                {dln}
              </div>
            )
          },
        }
      case 'Trademark Name':
        return {
          ...defaultCol,
          origHeaderName: 'Trademark Name',
          headerName: 'Trademark Name',
          renderCell: (params) => {
            let len = params.row[col].length
            return (
              <p>{`${params.row[col].substring(0, len > 10 ? 10 : len)}${
                len > 10 ? '...' : ''
              }`}</p>
            )
          },
        }
      case 'Trademark #':
        return {
          ...defaultCol,
          minWidth: 160,
        }
      case 'Receipt #':
        return {
          ...defaultCol,
          sortable: activeSearch !== 'Orders',
          renderCell: (params) => {
            if (appUserRights.includes('ReceiptView')) {
              return (
                <span
                  className="dln-link"
                  tabIndex={params.row[col] ? 0 : -1}
                  onClick={() => {
                    history.push(`/accounting/receipt/${params.row[col]}`)
                  }}
                >
                  {params.row[col]}
                </span>
              )
            }
            return params.row[col]
          },
        }
      case 'Prior Name(s)':
        return {
          ...defaultCol,
          hide: true,
        }
      case 'Address':
      case 'Search Criteria':
      case 'Debtor':
      case 'Secured Party':
      case 'Additional Defendants':
        return {
          ...defaultCol,
          sortable: false,
        }
      case 'Status':
        return {
          ...defaultCol,
          renderCell: (params) => {
            if (typeof params.row[col] === 'object') {
              if (params.row[col]?.Description) {
                return (
                  <div
                    className={`red-flag-status-cell ${
                      params.row[col].RedFlagStatus ? 'alert' : ''
                    }`}
                  title={params.row[col].Description}
                  >
                    {params.row[col].Description}
                  </div>
                )
              } else {
                return params.row[col] && params.row[col][Object.keys(params.row[col])[0]]
              }
            }
            return params.row[col]
          },
          sortComparator:(v1, v2) => {
            if (v1.Description){
              return v1.Description.localeCompare(v2.Description)
            }
            else
            {
              return v1.localeCompare(v2)
            }
          }
        }
      default:
        if (col.indexOf('Date') !== -1) {
          return {
            ...defaultCol,
            type: 'date',
            sortComparator: (v1, v2) =>
              moment(v1, 'MM-DD-YYYY')
                .toDate()
                .getTime()
                .toString()
                .localeCompare(moment(v2, 'MM-DD-YYYY').toDate().getTime().toString()),
          }
        }
        if (col.indexOf('Amount') !== -1) {
          return {
            ...defaultCol,
            type: 'number',
            align: 'left',
            headerAlign: 'left',
            valueFormatter: (params) => {
              return `$${(params.value || 0)
                .toFixed(2)
                .toString()
                .replace(/\B(?=(\d{3})+(?!\d))/g, ',')}`
            },
          }
        }
        return defaultCol
    }
  }

  const searchColumns = useMemo(() => {
    if (searchResults[activeSearch] && searchResults[activeSearch].length > 0) {
      let columns = (Object.keys(searchResults[activeSearch][0]) || [])
        .filter((col) => col.toLowerCase() !== `sourceid`)
        .map((col) => {
          return specificColumn(col)
        })
      if (
        searchFields.Name !== 'UserSearch' &&
        searchFields.Name !== 'FacsimileSignatureSearch' &&
        searchFields.Name !== 'OrderSearch' &&
        searchFields.Name !== 'AthleteAgentSearch' &&
        searchFields.Name !== 'ServiceOfProcessSearch' &&
        searchFields.Name !== 'DLNSearch'
      ) {
        columns.unshift({
          field: 'actions',
          headerName: 'View',
          width: 70,
          disableColumnMenu: true,
          sortable: false,
          renderCell: (params) => {
            if (params) {
              return (
                <div style={{ margin: 'auto' }}>
                  <span title="View Record">
                    <IconButton
                      onClick={() => {
                        history.push(getRedirect(params.row))
                      }}
                      icon={<IconSearch />}
                      label="View Record"
                    />
                  </span>
                </div>
              )
            }
          },
        })
      }
      return columns
    }
    return []
  }, [searchResults, activeSearch, getRedirect])

  const resultRows = useMemo(() => {
    if (searchResults[activeSearch]) {
      let rows = searchResults[activeSearch].map((result, i) => ({
        ...result,
        id: i,
        actions: true,
      }))
      return rows
    }
    return []
  }, [searchResults, activeSearch])

  useEffect(() => {
    if (searchResults[activeSearch]) {
      window.setTimeout(() => {
        resizeTable()
      }, 100)
    }
  }, [searchResults, activeSearch])

  const drawerTitle = useMemo(() => {
    switch (searchFields.Name) {
      case 'BusinessEntitySearch':
        return selectedRow.row && selectedRow.row['Name'] && selectedRow.row['File #']
          ? `${selectedRow.row['Name']} (${selectedRow.row['File #']})`
          : 'Details'
      default:
        return 'Details'
    }
  }, [searchFields.Name, selectedRow])

  const drawerSubtitle = useMemo(() => {
    switch (searchFields.Name) {
      case 'BusinessEntitySearch':
        return selectedRow.row && selectedRow.row['Entity Type']
          ? `${selectedRow.row['Entity Type']}`
          : null
      default:
        return null
    }
  }, [searchFields.Name, selectedRow])

  useEffect(() => {
    setShowItemInfo(false)
  }, [searchFields.Name])

  useEffect(() => {
    setDownloadOnNextTick(false)

    if (csvSearchResults[activeSearch]) {
      //GetCSV(csvSearchResults[activeSearch])
    } else if (searchResults[activeSearch]) {
      //GetCSV(searchResults[activeSearch])
    }
  }, [searchResults, activeSearch, downloadOnNextTick])

  const downloadCsv = () => {
    if (isMiscSearch(searchFields)) {
      const { PageSize, PageNumber, ...csvParams } = lastUsedParameters
      GET_MISC_STAFF_SEARCH_CSV_RESULTS(
        { ...csvParams, ModuleId: searchFields.MiscellaneousModuleId },
        (response) => {
          const blob = new Blob([response], { type: `text/csv;charset=utf-8;` })
          const link = window.URL.createObjectURL(blob)

          const a = document.createElement('a')
          a.href = link
          a.download = `${searchFields.Title}-export-${format(new Date(), 'MM-dd-yyyy')}.csv`
          document.body.appendChild(a)
          a.click()
          a.remove()
        }
      )
      return
    }
    //if (serverPaginated) {
    BO_SEARCH_GET_CSV_RESULTS(lastUsedParameters, lastSearchName, (response) => {
      const blob = new Blob([response], { type: `text/csv;charset=utf-8;` })
      const link = window.URL.createObjectURL(blob)

      const a = document.createElement('a')
      a.href = link
      a.download = `${lastSearchName}-export-${format(new Date(), 'MM-dd-yyyy')}.csv`
      document.body.appendChild(a)
      a.click()
      a.remove()
    })
    //}
  }

  useEffect(() => {
    if (downloadOnNextTick) {
      downloader.current.download()
    }
  }, [downloadOnNextTick])

  return (
    <Body className="no-padding bo-search" showDrawer={showItemInfo}>
      <div style={{ display: 'flex', flexDirection: 'column', width: '100%' }}>
        <div style={{ display: 'flex', flexDirection: 'row', width: '100%' }}>
          <Sidebar setShowItemInfo={setShowItemInfo} match={match} />
          <InnerBody>
            {searchFieldsError ? (
              <div className="search-container">
                <div className="empty-placeholder-wrapper">
                  <ErrorBox style={{ maxWidth: '200px' }} />
                  <h3>Failed to load the search options for this module.</h3>
                </div>
              </div>
            ) : loading && (match?.params?.id || '').startsWith('misc-') ? (
              <div className="loader-wrapper bo">
                <Spinner className="page-loader" themeColor="dark-neutral" />
              </div>
            ) : (
              <SearchContainer
                searchFields={searchFields}
                fetchWorkItems={fetchWorkItems}
                setActiveSearch={setActiveSearch}
                setSearchFields={setSearchFields}
                setShowItemInfo={setShowItemInfo}
                triggerSearch={triggerSearch}
              />
            )}
            <Drawer
              showCloseButton
              show={showItemInfo}
              title={drawerTitle}
              subtitle={drawerSubtitle}
              onSidebarDismiss={() => {
                setShowItemInfo(false)
              }}
            >
              <SearchItemInfo
                selectedItem={isMiscSearch(searchFields) ? selectedRow : selectedRow.row}
                columns={selectedRow.columns}
                searchFields={searchFields}
                getRedirect={getRedirect}
                isMiscSearch={isMiscSearch(searchFields)}
                miscSearchTemplate={miscSearchTemplates[activeSearch] || []}
                sourceTypeParam={searchFields?.SourceTypeParam ?? ''}
                moduleId={searchFields?.MiscellaneousModuleId ?? null}
              />
            </Drawer>
          </InnerBody>
        </div>
        {loading && !(match?.params?.id || '').startsWith('misc-') ? (
          <div className="loader-wrapper bo">
            <Spinner className="page-loader" themeColor="dark-neutral" />
          </div>
        ) : (
          <div>
            {searchFields.Name !== 'DLNSearch' &&
              searchResults &&
              searchResults[activeSearch] &&
              searchResults[activeSearch].length > 0 && (
                <span title="Download CSV" style={{ float: 'right', marginRight: '20px' }}>
                  <IconButton
                    onClick={downloadCsv}
                    icon={<DownloadBox />}
                    label="Download CSV"
                    customCss={{
                      height: '35px',
                      width: '35px',
                      svg: { height: '30px', width: '30px', marginTop: '-7px' },
                    }}
                  />
                </span>
              )}
            <div ref={tableRef} className="table-container">
              {isMiscSearch(searchFields) && !error ? (
                <MiscSearchGrid
                  template={miscSearchTemplates[activeSearch] || []}
                  rows={searchResults[activeSearch] || []}
                  loading={serverPaginatedLoading}
                  page={serverPage[activeSearch] || 0}
                  pageSize={rowsPerPage}
                  rowCount={paginatedRowCounts[activeSearch] || 0}
                  rowsPerPageOptions={[10, 25, 50]}
                  onPageChange={(newPage) => {
                    setServerPaginatedLoading(true)
                    dispatch(
                      fetchAndSetMiscBOSearchResults(
                        {
                          ...lastUsedParameters,
                          ModuleId: searchFields.MiscellaneousModuleId,
                          PageNumber: newPage,
                          PageSize: rowsPerPage,
                        },
                        searchFields.Name,
                        () => setServerPaginatedLoading(false),
                        () => setServerPaginatedLoading(false)
                      )
                    )
                    dispatch({ type: SET_SERVER_PAGE, searchName: activeSearch, serverPage: newPage })
                  }}
                  onPageSizeChange={(newPageSize) => {
                    setServerPaginatedLoading(true)
                    dispatch(
                      fetchAndSetMiscBOSearchResults(
                        {
                          ...lastUsedParameters,
                          ModuleId: searchFields.MiscellaneousModuleId,
                          PageNumber: 0,
                          PageSize: newPageSize,
                        },
                        searchFields.Name,
                        () => setServerPaginatedLoading(false),
                        () => setServerPaginatedLoading(false)
                      )
                    )
                    dispatch({ type: SET_ROWS_PER_PAGE, newPageSize })
                    dispatch({ type: SET_SERVER_PAGE, searchName: activeSearch, serverPage: 0 })
                  }}
                  onSortModelChange={(sortModel) => {
                    setTriggerSearch({ trigger: true, sortModel })
                    sortModel &&
                      dispatch({ type: SET_SERVER_PAGE, searchName: activeSearch, serverPage: 0 })
                  }}
                  onRowClick={(row) => {
                    setShowItemInfo(true)
                    setSelectedRow(row)
                  }}
                  onViewRecord={(row) =>
                    history.push(
                      `/recordDetails/${searchFields?.SourceTypeParam ?? ''}/${row.RecordId}?moduleId=${searchFields?.MiscellaneousModuleId ?? ''}`
                    )
                  }
                />
              ) : !error &&
                searchResults &&
                searchResults[activeSearch] &&
                searchResults[activeSearch][0] ? (
                serverPagination[activeSearch] ? (
                  <React.Fragment>
                    {serverPaginatedLoading && (
                      <div className="inline-loader loader-wrapper bo">
                        <Spinner className="page-loader" themeColor="dark-neutral" />
                      </div>
                    )}
                    <GraniteDataGrid
                      columns={searchColumns}
                      getRowId={(r) => r.id}
                      rowCount={paginatedRowCounts[activeSearch] || 0}
                      rows={resultRows}
                      searchable
                      autoHeight
                      selectable
                      pagination
                      loading={serverPaginatedLoading}
                      page={serverPage[activeSearch] || 0}
                      onPageChange={(newPage) => {
                        setServerPaginatedLoading(true)
                        getSearchResults(
                          { ...searchParameters, PageSize: rowsPerPage, PageNumber: newPage + 1 },
                          searchFields.Name,
                          searchFields.Title
                        )
                        dispatch({
                          type: SET_SERVER_PAGE,
                          searchName: activeSearch,
                          serverPage: newPage,
                        })
                      }}
                      rowsPerPageOptions={[10, 25, 50]}
                      pageSize={rowsPerPage}
                      onPageSizeChange={(newPageSize) => {
                        setServerPaginatedLoading(true)
                        getSearchResults(
                          {
                            ...searchParameters,
                            PageSize: newPageSize,
                            PageNumber: (serverPage[activeSearch] || 0) + 1,
                          },
                          searchFields.Name,
                          searchFields.Title
                        )
                        dispatch({ type: SET_ROWS_PER_PAGE, newPageSize })
                      }}
                      onRowClick={(r) => {
                        setShowItemInfo(true)
                        setSelectedRow(r)
                      }}
                      paginationMode={'server'}
                      sortingMode="server"
                      onSortModelChange={(sortModel) => {
                        setTriggerSearch({ trigger: true, sortModel })
                        sortModel &&
                          dispatch({
                            type: SET_SERVER_PAGE,
                            searchName: activeSearch,
                            serverPage: 0,
                          })
                      }}
                    />
                  </React.Fragment>
                ) : (
                  <GraniteDataGrid
                    columns={searchColumns}
                    getRowId={(r) => r.id}
                    rows={resultRows}
                    searchable
                    autoHeight
                    selectable
                    pagination
                    page={page < searchResults[activeSearch].length / rowsPerPage ? page : 0}
                    onPageChange={(newPage) =>
                      dispatch({ type: SET_BO_SEARCH_RESULTS_PAGE, page: newPage })
                    }
                    pageSize={rowsPerPage}
                    rowsPerPageOptions={[10, 25, 50]}
                    onPageSizeChange={(newPageSize) =>
                      dispatch({ type: SET_ROWS_PER_PAGE, newPageSize })
                    }
                    onRowClick={(r) => {
                      setShowItemInfo(true)
                      setSelectedRow(r)
                    }}
                  />
                )
              ) : !error ? (
                searchResults[activeSearch] !== undefined && (
                  <EmptySearchPlaceholder searchTerm="" />
                )
              ) : (
                <div className="empty-placeholder-wrapper">
                  <ErrorBox style={{ maxWidth: '200px' }} />
                  <h3>Failed to retrieve search results from service.</h3>
                  <p>{errorDetails && (errorDetails.MessageDetail || errorDetails.Message)}</p>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
      <FileDownloader
        fileName={`${activeSearch}-export-${format(new Date(), 'MM-dd-yyyy')}.csv`} // change later to have selected category and timeframe with current date.
        href={downloadURL}
        ref={downloader}
      />
    </Body>
  )
}

export default BOSearch

const SearchContainer = ({
  searchFields,
  fetchWorkItems,
  setActiveSearch,
  setShowItemInfo,
  triggerSearch,
}) => {
  const [currentPortal, setCurrentPortal] = useState('')
  const [selectedDates, setSelectedDates] = useState([])
  const [searchObject, setSearchObject] = useState({})
  const [searchCriteriaAreValid, setSearchCriteriaAreValid] = useState(true)
  searchObject[searchFields.Name] = { ...searchObject[searchFields.Name] }
  const dispatch = useDispatch()
  const { appUserRights } = useSelector((state) => state.auth, shallowEqual)
  const { searchJson } = useSelector((state) => state.boSearch, shallowEqual)

  const components = {
    TextInput,
    SelectBox,
    Checkbox,
    RadioGroup,
    DatePicker,
    MultiSelectDropdown,
  }

  const clearSearchFields = () => {
    let defaultSearchObject = {}
    ;(searchFields.SearchFields || []).forEach((f) => {
      if (f.defaultSearchValue) {
        defaultSearchObject[f.name] = f.defaultSearchValue
      }
    })
    setSearchObject((prevObject) => ({
      ...prevObject,
      [searchFields.Name]: { ...defaultSearchObject },
    }))
    dispatch({ type: CLEAR_BO_SEARCH_ERRORS })
    setSearchCriteriaAreValid(true)
  }
  useEffect(() => {
    clearSearchFields()
  }, [searchFields])

  useEffect(() => {
    if (triggerSearch.trigger && triggerSearch.sortModel[0]) {
      submitSearch({
        ...searchObject[searchFields.Name],
        SortField: triggerSearch.sortModel[0]['field'],
        SortDirection: triggerSearch.sortModel[0]['sort'],
      })
    }
  }, [triggerSearch])

  const submitSearch = (parameters) => {
    Object.keys(parameters).forEach((key) => {
      if (!parameters[key]) {
        delete parameters[key]
      }
    })
    if (searchFields.SetParams) {
      parameters = { ...parameters, ...searchFields.SetParams }
    }

    let criteriaAreValid = true
    searchFields.SearchFields.forEach((field) => {
      if (field.type === 'DatePicker' && parameters[field.name]) {
        // validate date search criteria
        const value = parameters[field.name]
        if (value.length < 10 || !isValid(new Date(value))) {
          criteriaAreValid = false
        }
      }
    })

    if (criteriaAreValid) {
      setSearchCriteriaAreValid(true)
      setShowItemInfo(false)
      fetchWorkItems({ ...parameters }, searchFields.Name, searchFields.Title, true)
      dispatch({ type: SET_BO_SEARCH_RESULTS_PAGE, page: 0 })
    } else {
      setSearchCriteriaAreValid(false)
    }
  }

  useEffect(() => {
    setActiveSearch(isMiscSearch(searchFields) ? searchFields.Name : searchFields.Title)
  }, [setActiveSearch, searchFields.Title, searchFields.Name])

  const checkSubmit = (e) => {
    if (e.which === 13) {
      e.preventDefault()
      submitSearch(searchObject[searchFields.Name])
    }
  }

  const isFieldRenderConditionValid = (values, searchFieldsName, compareName) => {
    return !(
      (!searchObject[searchFieldsName][compareName] &&
        searchObject[searchFieldsName][compareName] !== 0) ||
      !values[compareName].includes(searchObject[searchFieldsName][compareName].toString())
    )
  }

  const renderField = (field) => {
    if (field.renderCompareConditions) {
      const values = field.renderCompareConditions
      const fieldNames = Object.keys(values)
      if (fieldNames.length > 0) {
        let returnValue = true
        fieldNames.forEach((compareName) => {
          if (!isFieldRenderConditionValid(values, searchFields.Name, compareName)) {
            if (searchObject[searchFields.Name][field.name]) {
              const dupFields = searchFields.SearchFields.filter(
                (item) => item.Name === field.name && item.id !== field.id
              )
              if (dupFields && dupFields.length > 0) {
                let isFieldNameRendered = false
                dupFields.forEach((dupField) => {
                  if (
                    isFieldRenderConditionValid(
                      dupField.renderCompareConditions,
                      searchFields.Name,
                      compareName
                    )
                  ) {
                    isFieldNameRendered = true
                  }
                })
                if (!isFieldNameRendered) {
                  delete searchObject[searchFields.Name][field.name]
                }
              } else {
                // remove search condition if not visible
                delete searchObject[searchFields.Name][field.name]
              }
            }
            returnValue = false
          }
        })
        return returnValue
      }
    }

    return true
  }

  const getField = (field, i) => {
    if (!renderField(field)) return null
    const FieldType = components[field.type]
    field = {
      ...field,
      onKeyPress: checkSubmit,
    }

    if (field.type === 'DatePicker') {
      const onChange = (value) => {
        if (typeof value === 'string') {
          let date = value && isValid(new Date(value)) ? new Date(value) : null
          if (value.length < 10) {
            setSearchObject((prevObject) => ({
              ...prevObject,
              [searchFields.Name]: { ...prevObject[searchFields.Name], [field.name]: value },
            }))
          } else {
            date
              ? setSearchObject((prevObject) => ({
                  ...prevObject,
                  [searchFields.Name]: {
                    ...prevObject[searchFields.Name],
                    [field.name]: format(date, 'MM/dd/yyyy'),
                  },
                }))
              : setSearchObject((prevObject) => ({
                  ...prevObject,
                  [searchFields.Name]: { ...prevObject[searchFields.Name], [field.name]: value },
                }))
            date && setSelectedDates([...selectedDates, { Name: field.name, value: date }])
          }
        }
        setSearchCriteriaAreValid(true)
      }

      const setFieldValue = (e) => {
        let v = e.target.value.replace(/\D/g, '')
        if (v.length >= 5) {
          v = `${v.slice(0, 2)}/${v.slice(2, 4)}/${v.slice(4)}`
        } else if (v.length >= 3) {
          v = `${v.slice(0, 2)}/${v.slice(2)}`
        }
        v = v.slice(0, 10)
        onChange(v)
      }

      const openPicker = () => {
        setCurrentPortal(field.name)
      }

      const closePicker = () => {
        setCurrentPortal('')
      }

      const isValidDate = searchObject[searchFields.Name][field.name]
        ? searchObject[searchFields.Name][field.name].length === 10 &&
          isValid(new Date(searchObject[searchFields.Name][field.name]))
          ? true
          : false
        : undefined

      return (
        <div key={i} className="date-picker-holder">
          <TextInput
            label={field.label}
            placeholder="MM/DD/YYYY"
            onChange={setFieldValue}
            value={searchObject[searchFields.Name][field.name] || ''}
            valid={isValidDate}
            // errorMessage={isValidDate === false ? 'Not a valid date' : undefined}
            onKeyPress={checkSubmit}
            errorMessage={isValidDate === false && 'Please enter a valid date'}
          />
          <button
            className={`btn btn-primary btn-raised picker-button ${
              isValidDate === false ? 'button-up' : ''
            }`}
            aria-label={`Open calendar widget for ${field.name}`}
            onClick={() => openPicker(field.name)}
          >
            <IconCalendar size="small" />
          </button>
          <FieldType
            {...field}
            selectedDate={
              isValidDate && searchObject[searchFields.Name][field.name]
                ? new Date(searchObject[searchFields.Name][field.name])
                : null
            }
            onChange={onChange}
            isOpen={currentPortal === field.name}
            onCalendarClose={closePicker}
          />
        </div>
      )
    }
    if (field.type === 'TextInput' && field.name === 'Name') {
      return (
        <div key={i} className="name-container">
          <FieldType
            {...field}
            onChange={(e) =>
              setSearchObject((prevObject) => ({
                ...prevObject,
                [searchFields.Name]: {
                  ...prevObject[searchFields.Name],
                  [field.name]: e.target.value,
                },
              }))
            }
            value={searchObject[searchFields.Name][field.name] || ''}
          />
          <Checkbox
            label="Name Contains"
            onChange={(value, e) =>
              setSearchObject((prevObject) => ({
                ...prevObject,
                [searchFields.Name]: {
                  ...prevObject[searchFields.Name],
                  NameContains: e.target.checked,
                },
              }))
            }
            checked={searchObject[searchFields.Name].NameContains || false}
          />
        </div>
      )
    }
    if (field.type === 'SelectBox') {
      let updateOptions = field.options || []
      if (field.apiCall) {
        if (field.apiCall === 'States') updateOptions = getStateOptions()
        if (field.apiCall === 'Countries') updateOptions = getCountryOptions()
      }
      field.placeholder = 'Select an Option'
      return (
        <FieldType
          key={i}
          {...field}
          onChange={(value, e) =>
            setSearchObject((prevObject) => ({
              ...prevObject,
              [searchFields.Name]: { ...prevObject[searchFields.Name], [field.name]: value },
            }))
          }
          noPlaceholder={field.defaultSearchValue ? true : undefined}
          value={searchObject[searchFields.Name][field.name] || ''}
          options={updateOptions}
        />
      )
    }
    if (field.type === 'Checkbox') {
      return (
        <FieldType
          key={i}
          {...field}
          onChange={(value, e) =>
            setSearchObject((prevObject) => ({
              ...prevObject,
              [searchFields.Name]: {
                ...prevObject[searchFields.Name],
                [field.name]: e.target.checked,
              },
            }))
          }
          checked={searchObject[searchFields.Name][field.name]}
        />
      )
    }
    if (field.type === 'RadioGroup') {
      return (
        <FieldType
          key={i}
          {...field}
          onChange={(e) =>
            setSearchObject((prevObject) => ({
              ...prevObject,
              [searchFields.Name]: { ...prevObject[searchFields.Name], [field.name]: e },
            }))
          }
          value={searchObject[searchFields.Name][field.name] || ''}
        />
      )
    }
    if (field.type === 'MultiSelectDropdown') {
      return (
        <MultiSelectDropdown
          key={i}
          label={field.label}
          placeholder={field.placeholder}
          options={field.options || []}
          value={searchObject[searchFields.Name][field.name] || []}
          onChange={(values) =>
            setSearchObject((prevObject) => ({
              ...prevObject,
              [searchFields.Name]: { ...prevObject[searchFields.Name], [field.name]: values },
            }))
          }
        />
      )
    }

    return (
      <FieldType
        key={i}
        {...field}
        onChange={(e) =>
          setSearchObject((prevObject) => ({
            ...prevObject,
            [searchFields.Name]: { ...prevObject[searchFields.Name], [field.name]: e.target.value },
          }))
        }
        value={searchObject[searchFields.Name][field.name] || ''}
      />
    )
  }

  // Misc Records searches are not in `searchJson` and key on `MiscModule{id}`, which has no
  // case in `doesUserHaveSearchAccess` (it would fall through to `false` and render Access
  // Denied). They are gated on `MiscellaneousView`, matching the Sidebar render check.
  const userHasSearchAccess = isMiscSearch(searchFields)
    ? appUserRights.includes('MiscellaneousView')
    : doesUserHaveSearchAccess(searchFields.Name, appUserRights)

  if (searchJson && searchJson.length > 0 && !userHasSearchAccess)
    return (
      <div className="search-container">
        <h1>
          {searchFields.Title}
          {searchFields.Title ? ' - ' : ''}Access Denied
        </h1>
      </div>
    )

  return (
    <div className="search-container">
      <h1>{searchFields.Title}</h1>
      {searchFields.Name == 'DLNSearch' ? (
        <div>
          <TextInput
            placeholder={searchFields.SearchFields[0].placeholder}
            onChange={(e) => {
              setSearchCriteriaAreValid(true)
              setSearchObject((prevObject) => ({
                ...prevObject,
                [searchFields.Name]: {
                  ...prevObject[searchFields.Name],
                  DLN: e.target.value.toUpperCase(),
                },
              }))
            }}
            value={
              searchObject[searchFields.Name].DLN
                ? searchObject[searchFields.Name].DLN.toUpperCase()
                : ''
            }
            valid={searchCriteriaAreValid === false ? false : undefined}
            errorMessage={
              searchCriteriaAreValid === false ? 'Please enter a valid DLN, including hyphens' : ''
            }
          />
          <div style={{ marginTop: '5px' }} className="button-container">
            <button
              className={`btn btn-primary search-button`}
              aria-label={`Submit Search`}
              onClick={() => {
                if (searchObject[searchFields.Name]['DLN']) {
                  dispatch(
                    fetchAndSetDLNSearchResults(
                      searchObject[searchFields.Name]['DLN'],
                      searchFields.Title,
                      (envelopeId) => {
                        setSearchCriteriaAreValid(true)
                        dispatch(
                          openSpecificImage(envelopeId, searchObject[searchFields.Name]['DLN'])
                        )
                        dispatch(setSuccessToast('View Result in Image Viewer'))
                      },
                      (dln) => {
                        dispatch(setErrorToast('DLN ' + dln + ' not found'))
                        setSearchCriteriaAreValid(false)
                      }
                    )
                  )
                } else {
                  setSearchCriteriaAreValid(false)
                  // dispatch(setErrorToast('Please enter a DLN'))
                }
              }}
            >
              <IconSearch />
              Search
            </button>
          </div>
        </div>
      ) : (
        searchFields.SearchFields && (
          <div>
            <div className="button-container">
              <button
                className={`btn btn-danger clear-button`}
                aria-label="Clear Search Fields"
                onClick={() => {
                  clearSearchFields()
                  dispatch({ type: SET_BO_SEARCH_RESULTS_PAGE, page: 0 })
                }}
              >
                <CrossIcon />
                Clear
              </button>
              <button
                className={`btn btn-primary search-button`}
                aria-label={`Submit Search`}
                onClick={() => submitSearch(searchObject[searchFields.Name])}
              >
                <IconSearch />
                Search
              </button>
            </div>
            <div className="row">
              <div className="col">
                {searchFields &&
                  searchFields.SearchFields &&
                  searchFields.SearchFields.filter((field) => field.column === 1).map((field, i) =>
                    getField(field, i)
                  )}
              </div>
              <div className="col">
                {searchFields &&
                  searchFields.SearchFields &&
                  searchFields.SearchFields.filter((field) => field.column === 2).map((field, i) =>
                    getField(field, i)
                  )}
              </div>
            </div>
            {!searchCriteriaAreValid && (
              <div className="search-criteria-error-message">Errors in Search Criteria</div>
            )}
          </div>
        )
      )}
    </div>
  )
}

export const Sidebar = ({ match, setShowItemInfo }) => {
  const dispatch = useDispatch()
  const { appUserRights } = useSelector((state) => state.auth, shallowEqual)
  const { searchJson } = useSelector((state) => state.boSearch, shallowEqual)

  const filteredSearchCategories = useMemo(() => {
    return (searchJson || []).map((category) => {
      return {
        ...category,
        searches: category.searches.filter((search) => {
          return doesUserHaveSearchAccess(search.Name, appUserRights)
        }),
      }
    })
  }, [searchJson, appUserRights])

  const miscRoutes = useMemo(() => {
    return SUBROUTES.filter(
      (r) => r.IsMiscRecordsType && appUserRights.includes('MiscellaneousView')
    )
  }, [appUserRights])

  // Merge the searchJson categories and the Misc Records source-type tabs into a single
  // alphabetically-sorted list so the entire left nav stays in order — Misc tabs interleave
  // with the existing categories rather than being appended at the bottom. Empty categories
  // (no accessible searches) are dropped so each rendered accordion's position equals its
  // section index. A fresh array is sorted (not the memoized/redux arrays) to avoid mutation.
  const sections = useMemo(() => {
    const categorySections = (filteredSearchCategories || [])
      .filter((category) => category.searches.length > 0)
      .map((category) => ({ type: 'category', title: category.category, category }))
    const miscSections = miscRoutes.map((route) => ({
      type: 'misc',
      title: route.label || route.Title || '',
      route,
    }))
    return [...categorySections, ...miscSections].sort((a, b) =>
      (a.title || '').localeCompare(b.title || '')
    )
  }, [filteredSearchCategories, miscRoutes])

  const computeOpenSection = () => {
    const id = match.params.id
    if (id && String(id).startsWith('misc-')) {
      const moduleId = id.slice(id.lastIndexOf('-') + 1)
      return sections.findIndex(
        (s) =>
          s.type === 'misc' &&
          s.route.MiscRecordsModules.some((m) => String(m.MiscellaneousModuleId) === moduleId)
      )
    }
    return sections.findIndex(
      (s) => s.type === 'category' && s.category.searches.some((x) => x.id == id)
    )
  }

  const [openSection, setOpenSection] = useState(computeOpenSection())

  // Re-derive the open accordion from the URL only on an actual navigation (the route `id`
  // changes) or the one-time searchJson load — NOT on every incidental re-render. `toggle`
  // calls `setShowItemInfo(false)` (BOSearch state), which re-renders the Sidebar; if this
  // effect re-ran then, `computeOpenSection()` on a `misc-*` URL always resolves back to that
  // module's section and would force the accordion open again, fighting a manual collapse.
  // (The granite Accordion is hybrid controlled/uncontrolled — the caret reflects its internal
  // state while the body follows the `isOpen` prop — so a forced `isOpen=true` reopens the body
  // while the caret shows closed: the flaky toggle the user observed.)
  const openSectionSyncRef = useRef({ id: undefined, hadSearchJson: false })
  useEffect(() => {
    const id = match?.params?.id
    const hasSearchJson = (searchJson || []).length > 0
    const idChanged = id !== openSectionSyncRef.current.id
    const searchJsonJustLoaded = hasSearchJson && !openSectionSyncRef.current.hadSearchJson
    if (idChanged || searchJsonJustLoaded) {
      openSectionSyncRef.current = { id, hadSearchJson: hasSearchJson }
      setOpenSection(computeOpenSection())
    }
  }, [match?.params?.id, searchJson])

  return (
    <aside className="body-sidebar bo">
      {sections.map((section, i) => {
        const isOpen = openSection === i
        const toggle = () => {
          setShowItemInfo(false)
          openSection === i ? setOpenSection(null) : setOpenSection(i)
        }
        // The granite Accordion is hybrid controlled/uncontrolled: its caret reflects the
        // component's internal open state (toggled on click) while its body follows the `isOpen`
        // prop, and its internal sync only forces the internal state to `true`, never back to
        // `false`. So when an accordion stops being the open one WITHOUT a click on it (opening
        // another, or a URL-driven re-derive), its caret stays open while the body closes, and
        // the next click then does the opposite of what's expected. Including `isOpen` in the
        // key remounts the Accordion when its open state changes, re-seeding the internal state
        // from the prop so caret and body always agree.
        if (section.type === 'category') {
          return (
            <Accordion
              isOpen={isOpen}
              key={`category-${section.title}-${i}-${isOpen}`}
              title={section.title}
              onChange={toggle}
            >
              {section.category.searches.map((route) => (
                <NavLink
                  activeClassName="active"
                  to={`/search/back-office/${route.id}`}
                  key={route.id}
                  onClick={() => {
                    dispatch({ type: CLEAR_BO_SEARCH_ERRORS })
                    dispatch({ type: SET_BO_SEARCH_RESULTS_PAGE, page: 0 })
                  }}
                >
                  {route.Title}
                </NavLink>
              ))}
            </Accordion>
          )
        }
        return (
          <Accordion
            isOpen={isOpen}
            key={`misc-${section.route.parameter}-${isOpen}`}
            title={section.title}
            onChange={toggle}
          >
            {section.route.MiscRecordsModules.map((module) => (
              <NavLink
                activeClassName="active"
                to={`/search/back-office/misc-${module.MiscellaneousModuleId}`}
                key={module.MiscellaneousModuleId}
                onClick={() => {
                  dispatch({ type: CLEAR_BO_SEARCH_ERRORS })
                  dispatch({ type: SET_BO_SEARCH_RESULTS_PAGE, page: 0 })
                }}
              >
                {module.DisplayName}
              </NavLink>
            ))}
          </Accordion>
        )
      })}
    </aside>
  )
}

const InnerBody = ({ children }) => <div className="inner-body">{children}</div>

const doesUserHaveSearchAccess = (searchName, appUserRights) => {
  switch (searchName) {
    case 'BusinessEntitySearch':
    case 'BusinessFilingSearch':
      return appUserRights.includes('BusinessView')
    case 'OrderSearch':
      return appUserRights.includes('OrdersView')
    case 'RegisteredAgentEntitySearch':
      return appUserRights.includes('RegisteredAgentView')
    case 'DataRequestSearch':
      return appUserRights.includes('DataRequestView')
    case 'ReceiptSearch':
      return appUserRights.includes('ReceiptView')
    case 'UCCInfoRequestEntitySearch':
    case 'UCCEntitySearch':
      return appUserRights.includes('UCCView')
    case 'ApostilleEntitySearch':
      return appUserRights.includes('ApostilleView')
    case 'FacsimileSignatureSearch':
      return appUserRights.includes('FacsimileView')
    case 'NotaryEntitySearch':
      return appUserRights.includes('NotaryView')
    case 'AthleteAgentSearch':
      return appUserRights.includes('AthleteAgentView')
    case 'CustomCorrespondenceSearch':
      return appUserRights.includes('ViewCorrespondence')
    case 'DLNSearch':
      return appUserRights.includes('BrowseImages')
    case 'ServiceOfProcessSearch':
      return appUserRights.includes('SopView')
    case 'UserSearch':
      return appUserRights.includes('ViewUserStaffSearch')
    case 'TrademarkEntitySearch':
      return appUserRights.includes('TrademarkView')
    case 'CharitableOrganizationSearch':
      return appUserRights.includes('CharityView')
    case 'LobbyistSearch':
      return appUserRights.includes('LobbyistView')
    case 'ContractorSearch':
      return appUserRights.includes('ContractorView')
    default:
      return false
  }
}
